import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Home.css';
import DoorSplit from '../components/DoorSplit';
import { getAuthToken, getAuthUser } from '../utils/authSession';

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'SmartStays - Slim toegangsbeheer en/of schoonmaak';

    const token = getAuthToken();
    const user = getAuthUser();
    const isNative = localStorage.getItem('isNativeApp') === 'true';

    if (token && user && !isNative) {
      try {
        const userData = JSON.parse(user);
        if (userData.role === 'ADMIN') {
          navigate('/admin');
        } else if (userData.role === 'VERHUURDER') {
          navigate('/dashboard');
        }
      } catch (e) {}
    }
  }, [navigate]);

  return (
    <div className="home-container">
      <Navbar />

      {/* Hero: links de belofte, rechts de deur zelf (binnen/buiten) in een boogvormig kader dat
          de vorm van het logo volgt. */}
      <section className="home-hero">
        <div className="home-hero-copy">
          <h1>{t('home.heroTitle')}</h1>
          <p className="home-hero-lead">{t('home.introText')}</p>
          <div className="home-hero-actions">
            <Link to="/selection" className="home-btn home-btn-primary">{t('home.startNow')}</Link>
            <Link to="/contact" className="home-btn home-btn-quiet">{t('home.requestQuote')}</Link>
          </div>
        </div>
        <div className="home-hero-door">
          <DoorSplit />
        </div>
      </section>

      {/* Wat we regelen: drie blokken naast elkaar (toegangsbeheer, schoonmaak, automation). */}
      <section className="home-services">
        <article className="service-block">
          <div className="service-visual">
            <svg className="service-visual-svg anim-keypad" viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Codeslot dat een gast binnenlaat">
              <rect x="170" y="22" width="140" height="216" rx="24" fill="#fff" stroke="#e3dccf" />
              <rect x="190" y="42" width="100" height="34" rx="10" fill="#f6f3ee" />
              <circle className="kp-dot kp-dot-1" cx="212" cy="59" r="5" fill="#12284a" />
              <circle className="kp-dot kp-dot-2" cx="232" cy="59" r="5" fill="#12284a" />
              <circle className="kp-dot kp-dot-3" cx="252" cy="59" r="5" fill="#12284a" />
              <circle className="kp-dot kp-dot-4" cx="272" cy="59" r="5" fill="#12284a" />
              {[0,1,2,3].map((r) => [0,1,2].map((c) => (
                <circle key={r+'-'+c} className={`kp-key kp-key-${r*3+c}`} cx={210+c*30} cy={110+r*32} r="11" fill="#ece6db" />
              )))}
              <g className="kp-badge">
                <circle cx="372" cy="130" r="30" fill="#12284a" />
                <path d="M358 130l10 10 18-20" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <g className="kp-cal">
                <rect x="70" y="100" width="64" height="60" rx="12" fill="#fff" stroke="#e3dccf" />
                <rect x="70" y="100" width="64" height="18" rx="12" fill="#1e88e5" />
                <rect x="70" y="110" width="64" height="8" fill="#1e88e5" />
                <rect x="84" y="130" width="12" height="10" rx="3" fill="#ece6db" />
                <rect x="104" y="130" width="12" height="10" rx="3" fill="#12284a" />
                <rect x="84" y="146" width="12" height="8" rx="3" fill="#ece6db" />
              </g>
            </svg>
          </div>
          <h2>{t('home.accessTitle')}</h2>
          <p>{t('home.accessText1')}</p>
          <p>{t('home.accessText2')}</p>
          <Link to="/selection?tab=toegangsbeheer" className="home-link">{t('home.discoverAccess')}</Link>
        </article>
        <article className="service-block">
          <div className="service-visual">
            <svg className="service-visual-svg anim-clean" viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Planning waarin een schoonmaakmoment tussen twee boekingen wordt ingepland">
              <rect x="60" y="24" width="360" height="212" rx="24" fill="#fff" stroke="#e3dccf" />
              <rect x="84" y="46" width="70" height="10" rx="5" fill="#ece6db" />
              <rect x="84" y="64" width="110" height="8" rx="4" fill="#f6f3ee" />
              <rect x="84" y="108" width="112" height="34" rx="10" fill="#12284a" />
              <rect x="302" y="108" width="94" height="34" rx="10" fill="#12284a" />
              <rect className="cl-block cl-block-1" x="204" y="108" width="90" height="34" rx="10" fill="#1e88e5" />
              <rect x="84" y="160" width="64" height="34" rx="10" fill="#ece6db" />
              <rect x="226" y="160" width="170" height="34" rx="10" fill="#ece6db" />
              <rect className="cl-block cl-block-2" x="156" y="160" width="62" height="34" rx="10" fill="#1e88e5" />
              <path className="cl-spark cl-spark-1" d="M249 76l4 10 10 4-10 4-4 10-4-10-10-4 10-4z" fill="#1e88e5" />
              <path className="cl-spark cl-spark-2" d="M276 88l2.5 6 6 2.5-6 2.5-2.5 6-2.5-6-6-2.5 6-2.5z" fill="#12284a" />
            </svg>
          </div>
          <h2>{t('home.cleaningTitle')}</h2>
          <p>{t('home.cleaningText1')}</p>
          <p>{t('home.cleaningText2')}</p>
          <Link to="/selection?tab=schoonmaak" className="home-link">{t('home.discoverCleaning')}</Link>
        </article>
        <article className="service-block">
          <div className="service-visual">
            <svg className="service-visual-svg anim-chart" viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Overzicht van boekingen en omzet dat groeit">
              <rect x="60" y="24" width="360" height="212" rx="24" fill="#fff" stroke="#e3dccf" />
              <rect x="84" y="46" width="70" height="10" rx="5" fill="#ece6db" />
              <rect x="84" y="64" width="110" height="8" rx="4" fill="#f6f3ee" />
              <rect className="ch-bar ch-bar-1" x="100" y="120" width="34" height="90" rx="8" fill="#ece6db" />
              <rect className="ch-bar ch-bar-2" x="150" y="120" width="34" height="90" rx="8" fill="#ece6db" />
              <rect className="ch-bar ch-bar-3" x="200" y="120" width="34" height="90" rx="8" fill="#12284a" />
              <rect className="ch-bar ch-bar-4" x="250" y="120" width="34" height="90" rx="8" fill="#ece6db" />
              <rect className="ch-bar ch-bar-5" x="300" y="120" width="34" height="90" rx="8" fill="#12284a" />
              <path className="ch-line" d="M108 100 C150 96 190 84 230 76 S300 56 352 52" fill="none" stroke="#1e88e5" strokeWidth="4" strokeLinecap="round" pathLength="1" />
              <circle className="ch-dot" cx="352" cy="52" r="7" fill="#1e88e5" />
              <g className="ch-pill">
                <rect x="352" y="150" width="52" height="30" rx="15" fill="#e6f0fb" />
                <circle cx="372" cy="165" r="6" fill="#1e88e5" />
              </g>
            </svg>
          </div>
          <h2>{t('home.heroPretextTagline')}</h2>
          <p>{t('home.heroPretextDescription')}</p>
          <Link to="/selection?tab=automation" className="home-link">{t('home.discoverAutomation')}</Link>
        </article>
      </section>

      {/* Begeleiding: rustige band, geen schreeuwerige banner. */}
      <section className="home-guidance">
        <div className="home-guidance-text">
          <h2>{t('home.freeInstallTitle')}</h2>
          <p>{t('home.freeInstallText')}</p>
        </div>
        <div className="home-guidance-actions">
          <Link to="/contact" className="home-btn home-btn-light">{t('home.requestQuote')}</Link>
          <Link to="/selection" className="home-btn home-btn-outline-light">{t('home.discoverOffer')}</Link>
        </div>
      </section>

      {/* Platform */}
      <section className="home-platform">
        <h2>{t('home.featuresTitle')}</h2>
        <div className="platform-grid">
          <Link to="/selection?tab=toegangsbeheer" className="platform-item platform-item-1">
            <span className="platform-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7 11V8a5 5 0 0 1 10 0v3"/><rect x="5" y="11" width="14" height="10" rx="3"/><circle cx="12" cy="16" r="1.4" fill="#fff"/></svg>
            </span>
            <span className="platform-copy">
              <h3>{t('home.feature1Title')}</h3>
              <p>{t('home.feature1Text')}</p>
            </span>
          </Link>
          <Link to="/selection?tab=automation" className="platform-item platform-item-2">
            <span className="platform-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="3"/><path d="M4 10h16M8 3v4M16 3v4"/><path d="M9 15l2 2 4-4"/></svg>
            </span>
            <span className="platform-copy">
              <h3>{t('home.feature2Title')}</h3>
              <p>{t('home.feature2Text')}</p>
            </span>
          </Link>
          <Link to="/selection?tab=schoonmaak" className="platform-item platform-item-3">
            <span className="platform-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8L12 3z"/><path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8z"/></svg>
            </span>
            <span className="platform-copy">
              <h3>{t('home.feature3Title')}</h3>
              <p>{t('home.feature3Text')}</p>
            </span>
          </Link>
          <Link to="/selection?tab=automation" className="platform-item platform-item-4">
            <span className="platform-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="3.5" y="4" width="17" height="16" rx="3"/><path d="M8 16v-3M12 16V9M16 16v-5"/></svg>
            </span>
            <span className="platform-copy">
              <h3>{t('home.feature5Title')}</h3>
              <p>{t('home.feature5Text')}</p>
            </span>
          </Link>
          <Link to="/download" className="platform-item platform-item-5">
            <span className="platform-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="7" y="3" width="10" height="18" rx="3"/><path d="M12 8v5m0 0l-2-2m2 2l2-2M10.5 17.5h3"/></svg>
            </span>
            <span className="platform-copy">
              <h3>{t('home.feature4Title')}</h3>
              <p>{t('home.feature4Text')}</p>
            </span>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
